import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import Checkbox from '@atlaskit/checkbox';
import classNames from 'classnames';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import { ErrorMessage, Field, Fieldset, ValidMessage } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/add';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { G500 } from '@atlaskit/theme/colors';
import { Flex, Box, Text } from '@atlaskit/primitives';
import Avatar from '@atlaskit/avatar';
import {
  createAtlassianAccountWithNameAndEmail,
  fetchAtlassianAccountIDByEmail,
  fetchAccountDetailsByIds,
} from '@atlassian/temp-proxies';
import { NoData } from '@css-xp/ppc-common';
import { isValidPrimaryContactDomain } from '@css-xp/ppc-utils';
import { SignUpBanner } from './sign-up-banner';
import { SuccessBanner } from './success-banner';
import './add-contact-fields.scss';

const VALIDATE_EMAIL_INPUT_DELAY = 600;
const VALIDATE_ATLASSIAN_EMAIL_DEBOUNCE_TIME = 1000;

interface AddContactProps {
  atlassianAccountIds: Array<string>;
  setAtlassianAccountIds: Dispatch<SetStateAction<Array<string>>>;
  atlassianAccountEmails: Array<string>;
  setAtlassianAccountEmails: Dispatch<SetStateAction<Array<string>>>;
  currentUserList?: Array<string>;
  userAlreadyExistText?: string;
  emailFieldText: string;
  nameFieldText: string;
  addButtonText: string;
  validateEmailField?: (_email: string) => string | undefined;
  isAddNewContactDisabled?: boolean;
  isExistingContactRemoveDisabled?: boolean;
  removableCustomerEmailList?: string[];
  fullNameFieldLabel?: string;
  emailFieldLabel?: string;
  version?: number;
  contactFieldName?: string;
  isContactMandatory?: boolean;
  isExistingContactsBeEditable?: boolean;
  validMessageText?: string;
  validPartnerDomains?: string[];
  setIsInvalidPrimaryContact?: Dispatch<SetStateAction<boolean>>;
  isInvalidPrimaryContact?: boolean;
  isSetAsPrimaryEnabled?: boolean;
  setIsSetAsPrimaryChecked?: Dispatch<SetStateAction<boolean>>;
}

enum AtlassianAccountStatus {
  FETCHED = 'FETCHED',
  FETCHING = 'FETCHING',
  NOT_FETCHED = 'NOT_FETCHED',
  CREATED = 'CREATED',
}

const DEFAULT_CONTACT_OBJECT = {
  email: '',
  name: '',
  atlassianAccountStatus: AtlassianAccountStatus.NOT_FETCHED,
  error: '',
  isEditable: true,
  nameError: false,
  removeDisabled: false,
};

interface Contact {
  email: string;
  name: string;
  atlassianAccountStatus: AtlassianAccountStatus;
  error: string;
  isEditable: boolean;
  nameError: boolean;
  removeDisabled: boolean;
  picture?: string;
  isCreatingAccount?: boolean;
}

export const validateEmailByAtlassianAccount = async (
  email: string,
  currentUserList: Array<string>,
  userAlreadyExistText: string,
  shouldFetchAccountDetails = false
) => {
  const responseObject = { id: '', error: '', displayName: '', picture: '' };
  try {
    const payload: { emails: Array<string> } = {
      emails: [email],
    };
    const response = await fetchAtlassianAccountIDByEmail(payload);
    const accountId = get(response, '[0].account_id', '');
    if (accountId && shouldFetchAccountDetails) {
      const accountData = await fetchAccountDetailsByIds([accountId]);
      if (accountData && accountData.length > 0) {
        responseObject.displayName = accountData[0].displayName;
        responseObject.picture = accountData[0].picture;
      }
    }
    if (accountId && currentUserList.includes(accountId)) {
      responseObject.error = userAlreadyExistText;
    } else {
      responseObject.id = accountId;
    }
  } catch (err) {
    responseObject.error = 'Failed to fetch Atlassian Account ID';
  }
  return responseObject;
};

const getDefaultUserList = ({
  atlassianAccountEmails,
  removeDisabled,
  removableCustomerEmailList,
  isExistingContactsBeEditable,
}: {
  atlassianAccountEmails: Array<string>;
  removeDisabled: boolean;
  removableCustomerEmailList?: string[];
  isExistingContactsBeEditable: boolean;
}) => {
  return atlassianAccountEmails.map((email) => {
    const isRemoveDisabled = (function () {
      // If removableCustomerEmailList is provided, then removeDisabled will be true for the emails present in the list
      if (removableCustomerEmailList && removableCustomerEmailList.length > 0) {
        return !removableCustomerEmailList.includes(email);
      }
      return removeDisabled;
    })();
    return {
      email,
      name: '',
      atlassianAccountStatus: email ? AtlassianAccountStatus.FETCHED : AtlassianAccountStatus.NOT_FETCHED,
      error: '',
      isEditable: !email || isExistingContactsBeEditable,
      nameError: false,
      removeDisabled: isRemoveDisabled,
    };
  });
};

export const AddContactFields = ({
  atlassianAccountIds,
  setAtlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
  currentUserList = [],
  userAlreadyExistText = '',
  emailFieldText,
  nameFieldText,
  addButtonText,
  validateEmailField,
  isAddNewContactDisabled = false,
  isExistingContactRemoveDisabled = false,
  removableCustomerEmailList = [],
  fullNameFieldLabel,
  emailFieldLabel,
  version,
  contactFieldName,
  isContactMandatory = true,
  isExistingContactsBeEditable = false,
  validMessageText,
  validPartnerDomains,
  setIsInvalidPrimaryContact,
  isInvalidPrimaryContact,
  isSetAsPrimaryEnabled,
  setIsSetAsPrimaryChecked,
}: AddContactProps) => {
  const [userList, setUserList] = useState<Array<Contact>>(
    getDefaultUserList({
      atlassianAccountEmails,
      removeDisabled: isExistingContactRemoveDisabled,
      removableCustomerEmailList,
      isExistingContactsBeEditable,
    })
  );
  const memoizedUserList = useMemo(() => cloneDeep(userList), [userList.length]);
  const isVersionTwo = version === 2;

  const [isChecked, setIsChecked] = useState(false);

  const validatePrimaryEmailDomain = (email: string, validDomains: string[] | null | undefined): string | undefined => {
    const isPrimaryContactEmailValid = isValidPrimaryContactDomain({ email: email, partnerDomains: validDomains });
    if (!isPrimaryContactEmailValid) {
      return `Email address must not have the same domain as the partner account or its associated accounts`;
    }
    return undefined;
  };

  const validateEmail = useCallback((email: string | undefined, isChecked?: boolean) => {
    if (!isContactMandatory && !email) return;
    if (!email) return 'Email address is required';
    if (!isEmail(email)) return 'Enter a valid email address';
    if (isChecked) return validatePrimaryEmailDomain(email, validPartnerDomains);
    if (validateEmailField) return validateEmailField(email);
  }, []);

  const delayedValidateEmail = useCallback(
    async (email: string | undefined) => {
      return new Promise((resolve) => setTimeout(resolve, VALIDATE_EMAIL_INPUT_DELAY)).then(() => {
        return validateEmail(email);
      });
    },
    [validateEmail]
  );

  const addNewContact = useCallback(() => {
    const clonedUserList = cloneDeep(userList);
    clonedUserList[clonedUserList.length - 1] = { ...clonedUserList[clonedUserList.length - 1], isEditable: false };
    setUserList([...clonedUserList, { ...DEFAULT_CONTACT_OBJECT }]);
    setAtlassianAccountIds([...atlassianAccountIds, '']);
    setAtlassianAccountEmails([...atlassianAccountEmails, '']);
  }, [atlassianAccountIds, setAtlassianAccountIds, atlassianAccountEmails, setAtlassianAccountEmails, userList]);

  const removeCustomerContact = useCallback(
    (idx) => {
      const newUserList = [...userList.slice(0, idx), ...userList.slice(idx + 1)];
      // Make last field edittable if last field is removed
      if (idx === userList.length - 1 && !newUserList[idx - 1].removeDisabled) {
        newUserList[idx - 1].isEditable = true;
      }
      setUserList(newUserList);
      setAtlassianAccountIds([...atlassianAccountIds.slice(0, idx), ...atlassianAccountIds.slice(idx + 1)]);
      setAtlassianAccountEmails([...atlassianAccountEmails.slice(0, idx), ...atlassianAccountEmails.slice(idx + 1)]);
    },
    [atlassianAccountIds, setAtlassianAccountIds, atlassianAccountEmails, setAtlassianAccountEmails, userList]
  );

  const validateEmailByAtlassianAccountCallback = useCallback(
    async (email: string, newUserList: Array<Contact>, idx: number, currentUserList: Array<string>) => {
      if (!validateEmail(email)) {
        const res = await validateEmailByAtlassianAccount(email, currentUserList, userAlreadyExistText, isVersionTwo);
        const clonedUserList = cloneDeep(newUserList);
        clonedUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHED;
        clonedUserList[idx].error = res.error;
        if (isVersionTwo) {
          clonedUserList[idx].name = res.displayName;
          clonedUserList[idx].picture = res.picture;
        }
        setAtlassianAccountIds((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = res.id;
          return clonedValue;
        });
        setAtlassianAccountEmails((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = email;
          return clonedValue;
        });
        setUserList(clonedUserList);
      }
    },
    [validateEmail, userAlreadyExistText, isVersionTwo, setAtlassianAccountIds, setAtlassianAccountEmails]
  );

  const debouncedValidateEmailByAtlassian = useMemo(
    () => debounce(validateEmailByAtlassianAccountCallback, VALIDATE_ATLASSIAN_EMAIL_DEBOUNCE_TIME),
    [validateEmailByAtlassianAccountCallback]
  );

  const handleEmailUpdate = useCallback(
    (email: string, idx: number) => {
      const newUserList = cloneDeep(userList);
      newUserList[idx].email = email;
      newUserList[idx].name = '';
      newUserList[idx].error = '';
      newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.NOT_FETCHED;
      if (!validateEmail(email)) {
        newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHING;
      }
      debouncedValidateEmailByAtlassian(email, newUserList, idx, currentUserList);
      setUserList(newUserList);
      setAtlassianAccountIds((prevValue) => {
        const clonedValue = cloneDeep(prevValue);
        clonedValue[idx] = '';
        return clonedValue;
      });
      setAtlassianAccountEmails((prevValue) => {
        const clonedValue = cloneDeep(prevValue);
        clonedValue[idx] = email;
        return clonedValue;
      });
      setIsInvalidPrimaryContact && setIsInvalidPrimaryContact(false);
      setIsChecked(false);
    },
    [debouncedValidateEmailByAtlassian, setAtlassianAccountIds, setAtlassianAccountEmails, userList, validateEmail]
  );

  const createAtlassianAccount = useCallback(
    async (idx: number) => {
      const newUserList = cloneDeep(userList);
      if (isVersionTwo) {
        newUserList[idx].isCreatingAccount = true;
        setUserList(newUserList);
      }

      try {
        if (userList[idx].name) {
          const res = await createAtlassianAccountWithNameAndEmail(userList[idx].email, userList[idx].name, 'wac');
          newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.CREATED;
          if (isVersionTwo) {
            newUserList[idx].isEditable = false; // so that we show the contact data readonly as per new deisgn
          }
          const clonedAtlassianAccountIds = cloneDeep(atlassianAccountIds);
          clonedAtlassianAccountIds[idx] = res.id;
          setAtlassianAccountIds(clonedAtlassianAccountIds);
          const clonedAtlassianAccountEmails = cloneDeep(atlassianAccountEmails);
          clonedAtlassianAccountEmails[idx] = userList[idx].email;
          setAtlassianAccountEmails(clonedAtlassianAccountEmails);
        } else {
          newUserList[idx].nameError = true;
        }
      } catch (error) {
        newUserList[
          idx
        ].error = `We couldn't create an Atlassian account for ${userList[idx].email}. Try again or contact Partner Service Desk`;
      }

      if (isVersionTwo) {
        newUserList[idx].isCreatingAccount = false;
      }
      setUserList(newUserList);
    },
    [
      userList,
      atlassianAccountIds,
      setAtlassianAccountIds,
      atlassianAccountEmails,
      setAtlassianAccountEmails,
      isVersionTwo,
    ]
  );

  const getAccountSignUpBanner = useCallback(
    (idx: number) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <SignUpBanner
            email={userList[idx].email}
            handleCreateAtlassianAccount={() => createAtlassianAccount(idx)}
            version={version}
          />
        );
      }
    },
    [atlassianAccountIds, createAtlassianAccount, userList, version]
  );

  const getNameField = useCallback(
    (idx: number) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <Field
            name={`${contactFieldName ? contactFieldName : 'customer-contact'}-name[${idx}]`}
            isRequired
            defaultValue={memoizedUserList[idx].name}
            label={fullNameFieldLabel ?? ''}
          >
            {({ fieldProps }) => (
              <>
                <Textfield
                  {...fieldProps}
                  testId={`${contactFieldName ? contactFieldName : 'customer-contact'}-name-${idx}`}
                  autoComplete="off"
                  placeholder={nameFieldText}
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    const newUserList = cloneDeep(userList);
                    newUserList[idx].name = event.currentTarget.value;
                    newUserList[idx].nameError = !event.currentTarget.value;
                    setUserList(newUserList);
                    fieldProps.onChange(event);
                  }}
                />
                {userList[idx].nameError && <ErrorMessage>{'Full name is required'}</ErrorMessage>}
              </>
            )}
          </Field>
        );
      }
    },
    [atlassianAccountIds, nameFieldText, userList, fullNameFieldLabel, contactFieldName]
  );

  const getCreateAtlassianAccountSuccessBanner = useCallback(
    (idx: number) => {
      if (userList[idx].atlassianAccountStatus === AtlassianAccountStatus.CREATED) {
        return <SuccessBanner email={userList[idx].email} version={version} />;
      }
    },
    [userList, version]
  );

  const getElementAfterEmailInput = useCallback(
    (idx) => {
      if (userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHING) {
        return (
          <div className="input-after-icon">
            <Spinner size="small" />
          </div>
        );
      } else if (atlassianAccountIds[idx] && !isVersionTwo) {
        return (
          <div className="input-after-icon">
            <EditorSuccessIcon size="medium" primaryColor={G500} label="" />
          </div>
        );
      }
      return null;
    },
    [atlassianAccountIds, userList, isVersionTwo]
  );

  const getRemoveContactButton = useCallback(
    (idx) => {
      if (
        isVersionTwo &&
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return null; // since we are showing cancel button just below the create account button we don't need remove button
      }

      return (
        <Button
          appearance={isVersionTwo ? 'subtle' : 'link'}
          testId={`remove-button-${idx}`}
          onClick={() => removeCustomerContact(idx)}
          className="remove-button"
          iconBefore={isVersionTwo ? <TrashIcon label="remove" /> : undefined}
        >
          {!isVersionTwo ? 'Remove' : ''}
        </Button>
      );
    },
    [isVersionTwo, atlassianAccountIds, userList, removeCustomerContact]
  );

  const getCreateCancelAtlassianAccountButtons = useCallback(
    (idx) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <Box paddingBlockStart="space.150">
            <Flex justifyContent="end">
              <Button
                appearance="subtle"
                testId={`cancel-create-account-${userList[idx].email}`}
                onClick={() => {
                  const newUserList = cloneDeep(userList);
                  newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.NOT_FETCHED;
                  setUserList(newUserList);
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                appearance="primary"
                testId={`create-account-${userList[idx].email}`}
                onClick={() => {
                  createAtlassianAccount(idx);
                }}
                isDisabled={userList[idx].isCreatingAccount}
                isLoading={userList[idx].isCreatingAccount}
              >
                Create Atlassian account
              </LoadingButton>
            </Flex>
          </Box>
        );
      }
    },
    [atlassianAccountIds, createAtlassianAccount, userList]
  );

  React.useEffect(() => {
    if (isSetAsPrimaryEnabled) {
      const validationResult = validateEmail(atlassianAccountEmails[0], isChecked);
      if (atlassianAccountEmails[0] !== '' && validationResult) {
        setUserList((prevUserList) => {
          const updatedUserList = [...prevUserList];
          updatedUserList[0] = { ...updatedUserList[0], error: validationResult };
          return updatedUserList;
        });
        setIsInvalidPrimaryContact && setIsInvalidPrimaryContact(true);
      }
      setIsSetAsPrimaryChecked && setIsSetAsPrimaryChecked(isChecked);
    }
  }, [isChecked]);

  return (
    <div className="add-contact-container">
      {userList.map((user, idx) => {
        if (user.isEditable) {
          return (
            <Fieldset key={idx}>
              <div className="contact-field">
                <div>
                  <Field
                    name={`${contactFieldName ? contactFieldName : 'customer-contact'}-email[${idx}]`}
                    isRequired={isContactMandatory}
                    validate={(email) => delayedValidateEmail(email)}
                    defaultValue={memoizedUserList[idx].email}
                    label={emailFieldLabel ?? ''}
                  >
                    {({ fieldProps, error }) => {
                      return (
                        <>
                          <div className="add-contact">
                            <Textfield
                              {...fieldProps}
                              testId={`${contactFieldName ? contactFieldName : 'customer-contact'}-email-${idx}`}
                              autoComplete="off"
                              placeholder={emailFieldText}
                              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                handleEmailUpdate(event.currentTarget.value, idx);
                                fieldProps.onChange(event);
                              }}
                              elemAfterInput={getElementAfterEmailInput(idx)}
                            />
                          </div>
                          {isVersionTwo && atlassianAccountIds[idx] && !isInvalidPrimaryContact && (
                            <ValidMessage>{validMessageText || 'Valid customer contact'}</ValidMessage>
                          )}
                          {error && <ErrorMessage>{error}</ErrorMessage>}
                        </>
                      );
                    }}
                  </Field>
                  {!isVersionTwo && getNameField(idx)}
                  {getAccountSignUpBanner(idx)}
                  {isVersionTwo && getNameField(idx)}
                  {isVersionTwo && getCreateCancelAtlassianAccountButtons(idx)}
                  {getCreateAtlassianAccountSuccessBanner(idx)}
                  {user.error && <ErrorMessage testId={`error-in-fetching-${idx}`}>{user.error}</ErrorMessage>}
                </div>
                {idx > 0 && getRemoveContactButton(idx)}
              </div>
            </Fieldset>
          );
        } else if (isVersionTwo) {
          return (
            <Box paddingBlockEnd="space.150" testId={`user-info-section-${user.email}`}>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex alignItems="center" gap="space.150">
                  <Avatar size="small" src={user?.picture} />
                  <Flex direction="column">
                    <Text>{user.name || <NoData />}</Text>
                    <Text size="small" color="color.text.subtle">
                      {user.email}
                    </Text>
                  </Flex>
                </Flex>
                {!user.removeDisabled && atlassianAccountIds.length > 1 && getRemoveContactButton(idx)}
              </Flex>
              {getCreateAtlassianAccountSuccessBanner(idx)}
            </Box>
          );
        } else {
          return (
            <div key={idx} className="user-info-section">
              <div className="user-email" data-testId="user-email">
                {user.email}
              </div>
              {!user.removeDisabled && atlassianAccountIds.length > 1 && getRemoveContactButton(idx)}
            </div>
          );
        }
      })}

      {!isAddNewContactDisabled && (
        <div
          className={classNames('btn-add-another-email-address', {
            'btn-add-another-email-address-v2': isVersionTwo,
          })}
        >
          <Button
            appearance={isVersionTwo ? 'subtle' : 'link'}
            testId="add-new-customer-contact"
            iconBefore={<AddIcon label="Add icon" size="small" />}
            onClick={addNewContact}
            isDisabled={!atlassianAccountIds[atlassianAccountIds.length - 1]}
          >
            {addButtonText}
          </Button>
        </div>
      )}

      {isSetAsPrimaryEnabled && (
        <Checkbox
          value="default checkbox"
          isChecked={isChecked}
          label="Set as primary"
          onChange={() => {
            setIsChecked((current) => !current);
            setIsInvalidPrimaryContact && setIsInvalidPrimaryContact(false);
            setUserList((prevUserList) => {
              const updatedUserList = [...prevUserList];
              updatedUserList[0] = { ...updatedUserList[0], error: '' };
              return updatedUserList;
            });
          }}
          name="checkbox-default"
          testId="set-primary-checkbox"
        />
      )}
    </div>
  );
};
